import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Plus, Clock, Package, Boxes } from 'lucide-react';
import api from '../services/api';
import Quotes from '../pages/Quotes';
import Proforma from '../pages/Proforma';
import B29StockTransfer from '../pages/B29StockTransfer';
import HubTileArt from '../components/HubTileArt';
import { StockRequestModal } from '../components/StockRequestModal';
import { ContactNotesTasksModal } from '../components/ContactNotesTasksModal';
import { useZohoSession } from '../contexts/ZohoSessionContext';
import { useV3Shell } from '../v3/V3ShellContext';
import InBody from '../v3/InBody';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STAFFOLOGY_URL = 'https://app.staffologyhr.co.uk/portal/logoff.aspx';

/**
 * The key links at the top of a Space's home page. They take the place of the notification boxes: ten square tiles in the same 1400px row and the same gaps, so
 * every two squares are as wide as one notification box and line up with it. Each tile is drawn like the Home page tiles (an illustrated glass panel with its name
 * under it) in the new UI's glass, with an accent colour of its own (the colour of its link on the side bar) that glows at the edge. A tile that is a drop-down turns over
 * when pressed (like a card) and shows its links on its back. Each is only shown to people who may use it, with the same permission checks as the side bar and the old Home cards.
 * (`view` says which Space this is: the same links for the Sales Space and the Admin Space for now.)
 */
// `slots` (optional): a page layout's choice of which key links show, in which order and under what name: [{ key, label? }]. Without it all of them show.
export default function SpaceLinkCards({ user, view = null, slots = null }) {
  const navigate = useNavigate();
  const { requireConfirmed } = useZohoSession();
  const v3Shell = useV3Shell();
  const dark = v3Shell ? v3Shell.theme === 'dark' : user?.home_theme === 'dark';
  const rowRef = useRef(null);
  const [openMenu, setOpenMenu] = useState(null);
  const [modal, setModal] = useState(null); // { kind: 'quote' | 'proforma', mode } | { kind: 'b29' | 'stockrequest' | 'cm' }
  const [crmOk, setCrmOk] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get(`${API}/zoho/my-status`)
      .then((res) => { if (!cancelled) setCrmOk(!!(res.data?.is_linked || res.data?.is_super_admin)); })
      .catch(() => { if (!cancelled) setCrmOk(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const onDown = (e) => { if (rowRef.current && !rowRef.current.contains(e.target)) setOpenMenu(null); };
    const onKey = (e) => { if (e.key === 'Escape') setOpenMenu(null); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, []);

  const isSuper = user?.role === 'super_admin';
  const allow = (key) => isSuper || user?.[key] !== false; // on unless switched off for the person
  const need = (key) => isSuper || !!user?.[key];           // off unless given to the person

  const go = (route) => { setOpenMenu(null); navigate(route); };
  const open = (next) => { setOpenMenu(null); setModal(next); };

  // art = the illustration (components/HubTileArt.jsx); accent = the tile's own colour (the colour of its link on the side bar)
  const cards = [
    need('can_access_transport') && { id: 'transport', label: 'Transport', art: 'transport', accent: '#0ea5e9', onClick: () => { sessionStorage.setItem('transport_selected_date', new Date().toISOString()); go('/transport'); } },
    need('can_access_goods_in') && { id: 'goods-in', label: 'Goods In', art: 'goods-in', accent: '#0d9488', onClick: () => go('/goods-in') },
    { id: 'customers', label: 'Customers', art: 'crm', accent: '#2563eb', off: crmOk === false, title: crmOk === false ? 'CRM access is not enabled for your account' : 'Customer Management', onClick: () => { if (crmOk === false) return; if (!requireConfirmed()) return; open({ kind: 'cm' }); } },
    need('can_view_project_tracking') && { id: 'projects', label: 'Projects', art: 'm45', accent: '#7a2182', title: 'Project Tracking', onClick: () => go('/project-tracking') },
    allow('can_access_quotes') && { id: 'quotes', label: 'Quotes', art: 'quotes', accent: '#ee7203', menu: [
      { id: 'create', label: 'Create New', Icon: Plus, onClick: () => open({ kind: 'quote', mode: 'create' }) },
      { id: 'recent', label: 'Recent Quotes', Icon: Clock, onClick: () => open({ kind: 'quote', mode: 'recent' }) },
    ] },
    allow('can_access_proforma') && { id: 'proforma', label: 'Pro Forma', art: 'proforma', accent: '#e11d48', menu: [
      { id: 'create', label: 'Create New', Icon: Plus, onClick: () => open({ kind: 'proforma', mode: 'create' }) },
      { id: 'recent', label: 'Recent Pro Formas', Icon: Clock, onClick: () => open({ kind: 'proforma', mode: 'recent' }) },
    ] },
    allow('can_access_stock_transfer') && { id: 'stock-transfers', label: 'Stock Transfers', art: 'stock-transfer', accent: '#16a34a', menu: [
      { id: 'transfers', label: 'Stock Transfers', Icon: Package, onClick: () => go('/stock-transfer') },
      { id: 'b29', label: 'B29 Transfer', Icon: Boxes, onClick: () => open({ kind: 'b29' }) },
    ] },
    { id: 'stock-requests', label: 'Stock Requests', art: 'stock-requests', accent: '#84cc16', onClick: () => open({ kind: 'stockrequest' }) },
    allow('can_access_po_expenses') && { id: 'po-expenses', label: 'P.O. Expenses', art: 'po-expenses', accent: '#c026d3', onClick: () => go('/po-expenses') },
    need('can_access_staffology') && { id: 'staffology', label: 'Staffology', art: 'staffology', accent: '#cb52e1', onClick: () => { setOpenMenu(null); window.open(STAFFOLOGY_URL, '_blank', 'noopener,noreferrer'); } },
    // PI Counts is a tile that can be added (Spaces Builder > Link tiles), but is out of view until a page layout turns it on.
    need('can_access_pi_counts') && { id: 'pi-counts', label: 'PI Counts', art: 'pi-counts', accent: '#06b6d4', hiddenByDefault: true, onClick: () => go('/pi-counts') },
  ].filter(Boolean);

  // A page layout (Spaces Builder) can choose which of the key links show, in which order, and what each is called. A link the person may not use is left out as always.
  const shown = Array.isArray(slots)
    ? slots.filter((slot) => !slot.hidden).map((slot) => { const card = cards.find((candidate) => candidate.id === slot.key); return card ? { ...card, label: (slot.label || '').trim() || card.label } : null; }).filter(Boolean)
    : cards.filter((card) => !card.hiddenByDefault);

  const close = () => setModal(null);
  return (
    <>
      <div ref={rowRef} className="v3-sp2-row" data-testid="space-link-cards" data-space-view={view || undefined}>
        {shown.map((card) => (
          <div key={card.id} className={`v3-sp2-slot ${card.menu && openMenu === card.id ? 'v3-sp2-slot--flipped' : ''}`}>
            <button
              type="button"
              className={`v3-sp2-card ${card.off ? 'v3-sp2-card--off' : ''} ${openMenu === card.id ? 'v3-sp2-card--open' : ''}`}
              style={{ '--sc': card.accent }}
              onClick={card.menu ? () => setOpenMenu((cur) => (cur === card.id ? null : card.id)) : card.onClick}
              title={card.title || card.label}
              aria-expanded={card.menu ? openMenu === card.id : undefined}
              data-testid={`space-card-${card.id}`}
            >
              <span className="v3-sp2-art">
                <HubTileArt artKey={card.art} theme={dark ? 'dark' : 'light'} className="v3-sp2-svg" />
                {card.menu && <ChevronDown className="v3-sp2-caret" />}
              </span>
              <span className="v3-sp2-label"><span className="v3-sp2-name">{card.label}</span></span>
            </button>
            {/* The back of the card: the drop-down's links, filling the card. It is turned away (and not reachable) until the card is pressed and turns over. */}
            {card.menu && (
              <div
                className="v3-sp2-back"
                role="group"
                aria-label={`${card.label} links`}
                aria-hidden={openMenu === card.id ? undefined : true}
                data-testid={`space-menu-${card.id}`}
                style={{ '--sc': card.accent }}
                onClick={(e) => { if (e.target === e.currentTarget) setOpenMenu(null); }}
              >
                {card.menu.map((item) => (
                  <button key={item.id} type="button" tabIndex={openMenu === card.id ? 0 : -1} onClick={item.onClick} data-testid={`space-menu-${card.id}-${item.id}`}>
                    <item.Icon className="v3-sp2-menu-icon" />
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Inside UI V3 these windows are drawn on the page body, not inside the page (see InBody). */}
      <InBody>
        {modal && modal.kind === 'quote' && <Quotes.Modal isOpen onClose={close} mode={modal.mode} user={user} />}
        {modal && modal.kind === 'proforma' && <Proforma.Modal isOpen onClose={close} mode={modal.mode} user={user} />}
        <StockRequestModal isOpen={!!modal && modal.kind === 'stockrequest'} onClose={close} />
        <B29StockTransfer.Modal isOpen={!!modal && modal.kind === 'b29'} onClose={close} user={user} />
        <ContactNotesTasksModal isOpen={!!modal && modal.kind === 'cm'} onClose={close} />
      </InBody>
    </>
  );
}
